import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { type TreeNode } from '../tree-utils/tree-node.interface';
import { markSelectedPath, shortenTree } from '../tree-utils/tree.utils';
import { OptionListGroupComponent } from './option-list-group.component';
import { OptionListItemComponent } from './option-list-item.component';
import { OptionListComponent } from './option-list.component';

@Component({
  selector: 'rcp-option-list-tree',
  template: `
    <rcp-option-list>
      @for (option of treeOptions(); track $index) {
        @if (option.breadcrumbs?.length) {
          <rcp-option-list-item
            type="single"
            (toggle)="selectOption.emit(option.value)"
            [selected]="option.selected ?? false"
          >
            {{ option.label }}
            @for (breadcrumb of option.breadcrumbs; track breadcrumb) {
              / {{ breadcrumb }}
            }
          </rcp-option-list-item>
        } @else if (option.children?.length) {
          <rcp-option-list-group
            [label]="option.label"
            [selectable]="false"
            [withDivider]="false"
            [startExpanded]="option.selected ?? false"
          >
            <div class="rcp-tree-indent">
              <rcp-option-list-tree
                [options]="option.children"
                [value]="value()"
                [withDivider]="false"
                [nested]="true"
                (selectOption)="selectOption.emit($event)"
              />
            </div>
          </rcp-option-list-group>
        } @else {
          <rcp-option-list-item
            type="single"
            (toggle)="selectOption.emit(option.value)"
            [selected]="option.selected ?? false"
          >
            {{ option.label }}
          </rcp-option-list-item>
        }
        @if (withDivider()) {
          <div class="rcp-tree-divider"></div>
        }
      }
    </rcp-option-list>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--list-item-spacing);
      flex-grow: 1;
    }

    .rcp-tree-indent {
      padding-left: var(--section-padding);
    }

    .rcp-tree-divider {
      display: flex;
      padding: 0; /* enough spacing from items */
      &::after {
        content: '';
        width: calc(100% - calc(var(--item-padding-md) * 2));
        border-bottom: 1px solid var(--col-lines);
        margin-left: var(--item-padding-md);
        font-weight: var(--font-weight-normal);
      }
    }
  `,
  imports: [OptionListComponent, OptionListItemComponent, OptionListGroupComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OptionListTreeComponent {
  options = input<TreeNode[]>();
  withDivider = input<boolean>(true);
  nested = input<boolean>(false);

  value = input<any>();
  selectOption = output<any>();

  shortenedTreeOptions = computed(() => {
    const options = this.options();

    // for nested trees, the calculations are already done so we can show the options as is
    if (this.nested()) {
      return options;
    } else {
      // collapses items with only 1 child until the leaf is reached into 1
      // @ts-expect-error TS2345 argument not assignable
      return shortenTree(options);
    }
  });

  treeOptions = computed(() => {
    const value = this.value(),
      options = this.shortenedTreeOptions();

    return value ? markSelectedPath(value, options) : options;
  });
}
